import type { Session } from '@supabase/supabase-js'
import { getSupabaseBrowserClient, hasSupabaseBrowserConfig } from '../lib/supabaseClient'

type UserProfileRow = {
  id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  role: string | null
}

type GuestBookingRow = {
  id: string
  booking_reference: string
  room_name: string
  room_type: string
  check_in: string
  check_out: string
  guests_count: number
  guest_name: string | null
  guest_email: string | null
  total_amount: number | string | null
  payment_method: string | null
  status: string | null
  payment_status: string | null
  created_at: string | null
  updated_at: string | null
  special_requests: string | null
}

export type PortalUser = {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'guest' | 'admin'
}

export type PortalSessionState = {
  token: string
  user: PortalUser
}

export type PortalBooking = {
  id: string
  roomName?: string
  roomType?: string
  checkIn?: string
  checkOut?: string
  guests?: number
  confirmationNumber?: string
  guestName?: string
  guestEmail?: string
  amount?: number
  method?: string
  status?: string
  bookingStatus?: string
  paymentStatus?: string
  bookedDate?: string
  specialRequests?: string
}

const normalizeStatus = (value: unknown, fallback = 'pending') => String(value || fallback).toLowerCase()

const mapPortalUser = (row: UserProfileRow | null, fallback: { id: string; email: string; firstName?: string; lastName?: string }): PortalUser => ({
  id: row?.id || fallback.id,
  email: row?.email || fallback.email,
  firstName: row?.first_name || fallback.firstName || 'Guest',
  lastName: row?.last_name || fallback.lastName || '',
  role: row?.role === 'admin' ? 'admin' : 'guest',
})

const mapBookingRow = (row: GuestBookingRow): PortalBooking => ({
  id: String(row.booking_reference || row.id || ''),
  roomName: String(row.room_name || 'Marina Glen Stay'),
  roomType: String(row.room_type || ''),
  checkIn: String(row.check_in || '').split('T')[0],
  checkOut: String(row.check_out || '').split('T')[0],
  guests: Number(row.guests_count || 0),
  confirmationNumber: String(row.booking_reference || row.id || ''),
  guestName: String(row.guest_name || ''),
  guestEmail: String(row.guest_email || ''),
  amount: Number(row.total_amount || 0),
  method: String(row.payment_method || 'pending'),
  status: normalizeStatus(row.status),
  bookingStatus: normalizeStatus(row.status),
  paymentStatus: normalizeStatus(row.payment_status),
  bookedDate: String(row.created_at || row.updated_at || ''),
  specialRequests: String(row.special_requests || ''),
})

const loadPortalProfile = async (session: Session): Promise<PortalSessionState> => {
  const supabase = getSupabaseBrowserClient()
  const fallbackEmail = session.user.email || ''
  const { data, error } = await supabase
    .from('users')
    .select('id, email, first_name, last_name, role')
    .eq('auth_user_id', session.user.id)
    .maybeSingle()

  if (error) {
    throw error
  }

  return {
    token: session.access_token,
    user: mapPortalUser(data as UserProfileRow | null, {
      id: session.user.id,
      email: fallbackEmail,
      firstName: String(session.user.user_metadata?.first_name || ''),
      lastName: String(session.user.user_metadata?.last_name || ''),
    }),
  }
}

export const hasGuestPortalSupabase = (): boolean => hasSupabaseBrowserConfig()

export const getCurrentPortalSession = async (): Promise<PortalSessionState | null> => {
  if (!hasGuestPortalSupabase()) {
    return null
  }

  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.auth.getSession()

  if (error) {
    throw error
  }

  if (!data.session) {
    return null
  }

  return loadPortalProfile(data.session)
}

export const subscribeToPortalAuthChanges = (
  onChange: (sessionState: PortalSessionState | null) => void,
): (() => void) => {
  const supabase = getSupabaseBrowserClient()
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    void (async () => {
      try {
        onChange(session ? await loadPortalProfile(session) : null)
      } catch {
        onChange(null)
      }
    })()
  })

  return () => {
    data.subscription.unsubscribe()
  }
}

export const signInPortalUser = async (email: string, password: string): Promise<PortalSessionState> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })

  if (error || !data.session) {
    throw error || new Error('Unable to sign in.')
  }

  return loadPortalProfile(data.session)
}

const normalizeOrigin = (value?: string): string => {
  const candidate = value?.trim().replace(/^['"]|['"]$/g, '') || ''

  if (!candidate) {
    return ''
  }

  try {
    return new URL(candidate).origin
  } catch {
    return candidate.replace(/\/$/, '')
  }
}

const isLocalHost = (hostname: string): boolean => hostname === 'localhost' || hostname === '127.0.0.1'

const getPortalSiteOrigin = (): string => {
  if (typeof window !== 'undefined' && !isLocalHost(window.location.hostname)) {
    return window.location.origin
  }

  return normalizeOrigin(import.meta.env.VITE_BOOKING_URL)
    || normalizeOrigin(import.meta.env.VITE_SITE_URL)
    || normalizeOrigin(import.meta.env.NEXT_PUBLIC_SITE_URL)
    || (typeof window !== 'undefined' ? window.location.origin : '')
}

const buildPortalUrl = (path: string, query?: Record<string, string>): string => {
  const origin = getPortalSiteOrigin()
  const params = query ? new URLSearchParams(query).toString() : ''
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const targetPath = `${normalizedPath}${params ? `?${params}` : ''}`

  if (origin) {
    return `${origin}${targetPath}`
  }

  return targetPath
}

const getPortalRecoveryUrl = (): string => buildPortalUrl('/booking-system/reset-password')

const getPortalConfirmationUrl = (): string =>
  buildPortalUrl('/booking-system/login', {
    auth: 'confirmed',
  })

export const sendPortalPasswordResetEmail = async (email: string): Promise<void> => {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: getPortalRecoveryUrl(),
  })

  if (error) {
    throw error
  }
}

export const registerPortalGuest = async (payload: {
  firstName: string
  lastName: string
  email: string
  password: string
}): Promise<PortalSessionState> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.auth.signUp({
    email: payload.email,
    password: payload.password,
    options: {
      emailRedirectTo: getPortalConfirmationUrl(),
      data: {
        first_name: payload.firstName,
        last_name: payload.lastName,
        role: 'guest',
      },
    },
  })

  if (error) {
    throw error
  }

  if (data.session) {
    return loadPortalProfile(data.session)
  }

  try {
    return await signInPortalUser(payload.email, payload.password)
  } catch {
    throw new Error('Registration submitted. Check your email to confirm the account, then sign in.')
  }
}

export const signOutPortalUser = async (): Promise<void> => {
  if (!hasGuestPortalSupabase()) {
    return
  }

  const supabase = getSupabaseBrowserClient()
  await supabase.auth.signOut()
}

export const updatePortalProfile = async (firstName: string, lastName: string): Promise<PortalUser> => {
  const supabase = getSupabaseBrowserClient()
  const { data: authData, error: authError } = await supabase.auth.getUser()

  if (authError || !authData.user) {
    throw authError || new Error('You need to sign in again.')
  }

  const { error: profileError } = await supabase
    .from('users')
    .update({ first_name: firstName, last_name: lastName })
    .eq('auth_user_id', authData.user.id)

  if (profileError) {
    throw profileError
  }

  const { error: metadataError } = await supabase.auth.updateUser({
    data: {
      first_name: firstName,
      last_name: lastName,
    },
  })

  if (metadataError) {
    throw metadataError
  }

  const sessionState = await getCurrentPortalSession()
  if (!sessionState) {
    throw new Error('Unable to refresh your profile.')
  }

  return sessionState.user
}

export const changePortalPassword = async (email: string, currentPassword: string, newPassword: string): Promise<void> => {
  const supabase = getSupabaseBrowserClient()
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email,
    password: currentPassword,
  })

  if (signInError) {
    throw new Error('Your current password is incorrect.')
  }

  const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
  if (updateError) {
    throw updateError
  }
}

export const fetchPortalBookings = async (): Promise<PortalBooking[]> => {
  const supabase = getSupabaseBrowserClient()
  const { data, error } = await supabase.rpc('get_my_bookings')

  if (error) {
    throw error
  }

  return ((data || []) as GuestBookingRow[]).map(mapBookingRow)
}

export const updatePortalBookingRequests = async (bookingReference: string, specialRequests: string): Promise<void> => {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('update_my_booking_special_requests', {
    booking_reference_input: bookingReference,
    special_requests_input: specialRequests,
  })

  if (error) {
    throw error
  }
}

export const cancelPortalBooking = async (bookingReference: string): Promise<void> => {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('cancel_my_booking', {
    booking_reference_input: bookingReference,
  })

  if (error) {
    throw error
  }
}

export const deletePortalBooking = async (bookingReference: string): Promise<void> => {
  const supabase = getSupabaseBrowserClient()
  const { error } = await supabase.rpc('delete_my_booking', {
    booking_reference_input: bookingReference,
  })

  if (error) {
    throw error
  }
}